import { Ellipsoid } from '@skand/three-js-renderer/bin/private/math';
import { Matrix4, Quaternion, Vector3 } from 'three';
import { toCartographic } from '../../../Utils';
/**
 * Camera controls for the Panorama navigation mode.
 *
 * This is a special navigation mode as it does not have any
 * secondary controls. The user can only look around.
 *
 * Screen drag - Look around
 */
export class CameraPanorama {
    constructor(renderer, gestures) {
        Object.defineProperty(this, "renderer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "gestures", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "camera", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "pitchRange", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "pitch", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "yaw", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "defaultFOV", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "targetPosition", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "targetRotation", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "prevPosition", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "prevRotation", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "cameraMotionCallback", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "cameraStopCallback", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stopped", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.renderer = renderer;
        this.gestures = gestures;
        this.camera = this.renderer.getWorld().getCamera();
        this.pitchRange = Math.PI - 0.05;
        this.pitch = 0;
        this.yaw = 0;
        this.defaultFOV = this.camera.getPerspective().getFieldOfView();
        this.targetPosition = null;
        this.targetRotation = null;
        this.prevPosition = new Vector3();
        this.prevRotation = new Quaternion();
        this.cameraMotionCallback = () => { };
        this.cameraStopCallback = () => { };
        this.stopped = false;
    }
    /**
     * Clamp the pitch angle value
     */
    clampPitch() {
        const pitchMin = -(this.pitchRange / 2);
        const pitchMax = pitchMin + this.pitchRange;
        this.pitch = Math.max(Math.min(this.pitch, pitchMax), pitchMin);
    }
    /**
     * Handle camera rotation
     */
    applyTransforms() {
        this.clampPitch();
        const position = new Vector3();
        this.camera.getPositionECEF(position);
        const rotation = new Quaternion();
        this.camera.getRotation(rotation);
        const up = new Vector3();
        const tangent = new Vector3();
        const bitangent = new Vector3();
        Ellipsoid.WGS_84.rotationBasisForCartographic(toCartographic(position), null, up, tangent, bitangent);
        this.camera.setUpVector(up);
        const direction = tangent
            .clone()
            .applyAxisAngle(bitangent, this.pitch)
            .applyAxisAngle(up, this.yaw)
            .add(position);
        this.camera.lookAt(direction);
        this.cameraMotionCallback(position, rotation);
    }
    /**
     * Update pitch and yaw given the current configuration of the camera
     */
    updateRotationFromCamera() {
        // Calculate applied rotation axes
        const position = new Vector3();
        const rotation = new Quaternion();
        this.camera.getPositionECEF(position);
        this.camera.getRotation(rotation);
        const x = new Vector3(); // Camera right
        const y = new Vector3(); // Camera up
        const z = new Vector3(); // Camera forward
        new Matrix4().makeRotationFromQuaternion(rotation).extractBasis(x, y, z);
        const up = new Vector3(); // Globe surface normal
        const bitangent = new Vector3();
        Ellipsoid.WGS_84.rotationBasisForCartographic(toCartographic(position), null, up, undefined, bitangent);
        const projected = x.projectOnPlane(up);
        const crossed = new Vector3().crossVectors(bitangent, projected);
        this.yaw = projected.angleTo(bitangent) * Math.sign(up.dot(crossed));
        this.pitch = -y.angleTo(up);
        this.applyTransforms();
    }
    /**
     * Implement controls for zooming without moving the camera
     *
     * @param screenPosition
     * @param direction
     */
    handleCameraZoom(screenPosition, direction) {
        const minFOV = 15;
        const maxFOV = 90;
        const currentFOV = this.camera.getPerspective().getFieldOfView();
        this.camera
            .getPerspective()
            .setFieldOfView(Math.min(Math.max(currentFOV + direction * 0.1, minFOV), maxFOV));
    }
    /**
     * Implement controls first person camera
     */
    handleCameraDrag(screenPosition, prevPosition) {
        const diffX = screenPosition.x - prevPosition.x;
        const diffY = screenPosition.y - prevPosition.y;
        const angularScale = 0.005;
        this.pitch += diffY * angularScale;
        this.yaw += diffX * angularScale;
        this.applyTransforms();
    }
    /**
     * Handle camera stop event
     */
    updateCameraStop() {
        const position = new Vector3();
        const rotation = new Quaternion();
        this.camera.getPositionECEF(position);
        this.camera.getRotation(rotation);
        if (position.distanceTo(this.prevPosition) === 0 && rotation.angleTo(this.prevRotation) === 0) {
            if (!this.stopped) {
                this.cameraStopCallback(position, rotation);
                this.stopped = true;
            }
        }
        else {
            this.stopped = false;
        }
        this.camera.getPositionECEF(this.prevPosition);
        this.camera.getRotation(this.prevRotation);
    }
    start() {
        this.stop();
        this.gestures.register('tap', () => {
            this.targetPosition = null;
        });
        this.gestures.register('swipe', gesture => {
            this.handleCameraDrag(gesture.position, gesture.prevPosition);
        });
        this.gestures.register('zoom', gesture => {
            this.targetPosition = null;
            this.targetRotation = null;
            this.handleCameraZoom(gesture.position, gesture.direction.y);
        });
    }
    stop() {
        this.gestures.unregisterAll();
        this.camera.getPerspective().setFieldOfView(this.defaultFOV);
    }
    flyTo(position, rotation) {
        this.targetPosition = position;
        this.targetRotation = rotation;
    }
    moveTo(position, rotation) {
        this.camera.setPositionECEF(position);
        this.camera.setRotation(rotation);
        this.updateRotationFromCamera();
        this.applyTransforms();
    }
    setUnderground(underground) {
        // Do nothing since camera is stationary
    }
    setCameraMotionCallback(callback) {
        this.cameraMotionCallback = callback;
    }
    setCameraStopCallback(callback) {
        this.cameraStopCallback = callback;
    }
    updateFrame(deltaTime) {
        this.updateCameraStop();
        // Interpolate camera position
        if (this.targetPosition) {
            const currentPosition = new Vector3();
            this.camera.getPositionECEF(currentPosition);
            const displacement = this.targetPosition.clone().sub(currentPosition);
            const distance = displacement.length();
            const direction = displacement.multiplyScalar(1.0 / distance); // Normalize
            if (distance > 0.5) {
                const velocity = direction.clone().multiplyScalar(Math.max(1, distance / 10));
                this.camera.setPositionECEF(currentPosition.add(velocity));
            }
            else {
                this.camera.setPositionECEF(this.targetPosition);
                this.targetPosition = null;
            }
        }
        // Interpolate camera rotation
        if (this.targetRotation) {
            const currentRotation = new Quaternion();
            this.camera.getRotation(currentRotation);
            const angularDisplacement = currentRotation.angleTo(this.targetRotation);
            if (Math.abs(angularDisplacement) > 0.05) {
                this.camera.setRotation(currentRotation.slerp(this.targetRotation, 0.1));
            }
            else {
                this.camera.setRotation(this.targetRotation);
                this.targetRotation = null;
            }
            this.updateRotationFromCamera();
        }
    }
}
